import React, { useMemo, ReactNode } from 'react';
import { Link, NavLink, useParams } from 'react-router-dom';

// Components
import Breadcrumb from '../../container/breadcrumb';
import HeaderActions from '../../container/header_actions';
import PeriodBreadcrumb from '../../component/PeriodBreadcrumb';
import ConditionalRender from '../../helpers/ConditionalRender';
import FilterBar from '../../component/FilterBar';
import PageContent from '../../container/page_content';
import { OverflowTabbedMenu } from '../../container/overflow-tabbed-menu';

// Types
import HostTarget from '../../types/host/HostTarget';

// Constants
import { DATA_LOADING } from '../../component/Constants';

interface HostsTabbedProps {
    hosts: HostTarget[];
    toggleMenu: Function;
    applyPeriod: Function;
    setFilterOptions?: Function;
    refresh: () => void;
    loading?: number;
    activeTab: 'metrics' | 'instances' | 'events';
    title: string;
    description: string;
    children: ReactNode;
}

const HostsTabbed: React.FC<HostsTabbedProps> = ({
    hosts,
    toggleMenu,
    applyPeriod,
    setFilterOptions,
    refresh,
    loading,
    activeTab,
    title,
    description,
    children
}) => {
    const { hostId } = useParams();
    const host = hosts.find(item => item.id === Number(hostId));

    const navItems = useMemo(() => [
        {
            label: 'Host Metrics',
            icon: 'fal fa-server',
            path: `/hosts/${hostId}`,
            show: true,
            end: true,
        },
        {
            label: 'Database Activity',
            icon: 'fal fa-database',
            path: `/hosts/${hostId}/instances`,
            show: true,
            end: false,
        },
        {
            label: 'Events',
            icon: 'fal fa-exchange',
            path: `/hosts/${hostId}/events`,
            show: true,
            end: false,
        },
    ], [hostId]);

    return (
        <PageContent title={title} description={description}>
            {/* Heading */}
            <div id="top-header-wrapper" className="row row-cols-lg-3 row-cols-sm-1 row-cols-md-2">
                <Breadcrumb heading={host?.name || ''}
                            type="hosts"
                            hostId={Number(hostId)}>
                    <Link to="/hosts">Analysis</Link>
                    <Link to="/hosts">Hosts</Link>
                    <Link to={`/hosts/${hostId}`}>{host?.name}</Link>
                    <NavLink to={`/hosts/${hostId}`} className={"current-breadcrumb-link"}>
                        {activeTab === 'metrics' && 'Host metrics'}
                        {activeTab === 'instances' && 'Database activity'}
                        {activeTab === 'events' && 'Events'}
                    </NavLink>
                    <PeriodBreadcrumb />
                </Breadcrumb>
                <HeaderActions 
                    toggleMenu={toggleMenu} 
                    refresh={refresh}
                    applyPeriod={applyPeriod} 
                    setFilterOptions={setFilterOptions}
                />
            </div>

            <div className="loader">
                <ConditionalRender if={loading === DATA_LOADING}>
                    <div className="bar"/>
                </ConditionalRender>
            </div>

            {/* Filters */}
            <ConditionalRender if={setFilterOptions}>
                {setFilterOptions && <FilterBar setFilterOptions={setFilterOptions}/>}
            </ConditionalRender>

            {/* Tab Navigation */}
            <OverflowTabbedMenu items={navItems}/>

            {/* Page Content */}
            {children}
        </PageContent>
    );
};

export default HostsTabbed;
